import type { ProductDisplayDetails } from "proto/groceries";
import type { ImageDetails } from "proto/image";
import type { Observable } from "rxjs";
import type { LandingItemsLocation } from "../serv/locations";
import type { Vertical } from "$lib/def/verticals";
import type { Banner, View } from "proto/groceries_promotion";
import type { GetBuyAgainItemsResponse as GetBuyAgainItemsResponseProto } from "proto/groceries_micro_service";
import type { RequireAtLeastOne } from "$lib/def/util-types";

// ############################################################
// # GENERAL
// ############################################################

export type CacheMaxContext = {
	short: number,
	medium: number,
	long: number,
}

export type LandingItemContext = {
	base?: string,
	landingItems?: string,
	products?: string,
	cacheAge: CacheMaxContext
}

// ############################################################
// # FOOD
// ############################################################

export type FoodLandingItemsParams = LandingItemsLocation & {
	primary_food_filter?: string;
	secondary_filters?: string;
	deals?: string;
};

export interface Section<T = any> {
	type: LayoutSectionType;
	title?: string;
	endpoint?: string;
	data$?: Observable<T>;
}

export enum LayoutSectionType {
	FilterSelectors = 'filter_selectors',
	DepartmentSelectors = 'department_selectors',
	LargeBanners = 'large_banners',
	DeluxeBanner = 'deluxe_banner',
	SmallBanners = 'small_banners',
	BrandLogos = 'brand_logos',
	Swimlane = 'swimlane',
	AllRestaurants = 'all_restaurants',
}

export interface BaseLanding {
	layout: {
		type: LayoutSectionType;
		title?: string;
		endpoint?: string;
	}[];
	restaurants: {
		dynamic: string; // eg "/landing-items/v3/restaurants/dynamic?lat=-33.92&lon=18.70",
		semi_static: string; // eg "/landing-items/v3/restaurants/semi_static?lat=-33.92&lon=18.70"
		filtered: string; // eg "/landing-items/v3/restaurants/filtered?lat=-33.92&lon=18.70&primary_food_filter=Burgers"
	},
	deals: string; // eg "/landing-items/v3/deals?lat=-33.92&lon=18.70"
}

export interface RestaurantSemiStatic {
	id: number;
	name: string;
	description: string;
	classifications: { id:string, name:string, type:string }[];
	images: { restaurant_header: ImageDetails, restaurant_logo: ImageDetails };
	restaurant_group: { id: number, name: string, customer_facing_group_name: string };
	aliases: any[];
	address: {
		geo_location: { lon: number, lat: number },
		street_number: string;
		street_name: string;
		suburb: string;
		town: string;
		postal_code: string;
		// distance: number;
	};
	main_logo: string;
	tags: { id:number, name:string, type:'CUISINE'|'DIETARY' }[];
	distance: number;
	delivery_fee: number;
}
export interface RestaurantsSemiStatic {
	semi_static: RestaurantSemiStatic[];
}

export interface RestaurantDynamic {
	id: number;
	online: boolean;
	open: boolean;
	menu_id: number;
	vertical: Vertical,
	address: {
		deliverability_option: { delivery_available: true, option: string },
		location_id: number;
		distance: number;
		capacity_delay: number;
	};
	future_opening_time: { open_next_message: string, updated: number };
	delivery_fee: number;
	classifications: { id:string, name:string, type:string }[];
	restaurant_group: { id: number, name: string, customer_facing_group_name: string },
	statistics: {
		avg_food_quality_rating: number;
		number_of_ratings_str: string;
		rating_star_colour: string;
	},
	distance: number;
	prep_time: number;
	estimated_delivery_time_for_distance: number;
	tag_string: string;
	delivery_option: 'collect' | 'collect_and_delivery' | 'delivery';
	active_premium_rank: { _uuid: string, name: string, rank: string };
	sponsored?: boolean;
	indicators: {
		title: "FAST", // amend if needed
		main_image_url: string,
		background_color: string, // EG "#6dcff6",
		main_image: ImageDetails,
	}[],
}
export type Notification = {
	[type in 'capacity_delay'|'breakfast']:{title:string, sub_title:string, button_text?:string, button_image?:ImageDetails, refresh_delay:number}
}
export interface RestaurantsDynamic {
	dynamic: RestaurantDynamic[];
	tiles: (TileRestaurant | TileStack)[];
	notifications: Notification[];
}

export interface TileRestaurant {
	type: Vertical,
	payload: {
		id: number
	},
	sort_key?: number,
}
export interface TileStack {
	type: 'stack',
	payload: {
		title: string;
		restaurant_ids: number[];
		images: { restaurant_header: ImageDetails, restaurant_logo: ImageDetails };
		online: boolean;
		open: boolean;
		group_id: number;
		tag_string: string;
		active_premium_rank: { _uuid: string, name: string, rank: string };
		future_opening_time: { open_next_message: string, future_opening_ts: number };
	},
	sort_key?: number,
}

export interface RestaurantsFiltered {
	filtered: number[];
}
export interface PrimaryResultFilter {
	main_image: ImageDetails;
	main_image_url: string;
	name: string;
	type: 'deals' | 'filter' | 'link';
	term?: string;
	store_ids: number[];
  shop_department_url?: string,
}

export interface SecondaryResultFilter {
	name: string;
	type: 'filter';
	term: string;
}
export interface ResultFilters {
	primary_filters: PrimaryResultFilter[],
	secondary_filters: SecondaryResultFilter[];
}

export interface Note {
	type: string,
	title: string,
	sub_title: string,
	image: ImageDetails,
}

export type FilterType = string;

export interface Selector {
	filtering_type?: FilterType,
	label: string
}
export interface Deal extends Selector {
	_uuid?: string,
	restaurant_ids: number[],
	status?: 'not_on_special' | 'on_special', // for marketing_labels (show on deals page or not)
	sort?: number;
}

export type Deals = Record<FilterType, Deal[]> & { selectors: Selector[] };

export type BrandLogo = {
	restaurant_group_id: string,
	restaurant_ids: number[][],
	images: {
		restaurant_header: ImageDetails,
		restaurant_logo: ImageDetails,
	}
}

export type BrandLogos = {
	brand_logos: BrandLogo[]
}

export type SortType = "none" | "dynamic";

// ############################################################
// # GROC
// ############################################################

export type GrocLandingItemsParams = LandingItemsLocation & {
	store?: string;
};

export interface GrocLandingItemsAccumulator {
	/**
	 * A list of stores (for the filtered locaiton)
	 */
	stores: Store[];

	/**
	 * The current selected store.
	 */
	selectedStore: string | null;

	/**
	 * The definitive layout of the groceries screen.
	 */
	base: StoreLanding | null;
}


export enum GroceryLandingSection {
	Banners = 'large_banners',
	Aisles = 'aisles',
	Occasions = 'occasions',
	BuyAgain = 'buy_again',
	SavedItems = "saved_items",
	Deals = "deals_for_you",
}

export const GroceryBannerTypes = {
	Large_Banner: "large_banner",
	Occasion: "occasion",
} as const;

export type GroceryBannerTypes = Lowercase<keyof typeof GroceryBannerTypes>;

export interface Store {
	id: string; // eg "WC27"
	name: string; // eg "Test store (open + default)",
	open: boolean;
	end_to_end_open: boolean;
	deliverability: string; // eg "45 mins (2km)",
	liquor_availability: string; // eg "Sells liquor",
	operating_hours: string; // eg "00:00 - 23:59",
	default?: boolean;
	distance: number; // meters
	landing_notifications: {
		store_notification: string; // eg "Delivery in 45 mins"
		warning_subtitle: string; // eg "You can still fill up your cart and check out later."
		warning_title: string; // eg "Not accepting orders right now"
	}
}

export interface StoreLanding {
	layout: {
		type: GroceryLandingSection;
		title: string;
		sort: number;
		endpoint: string;
	}[];
	promotion_banner: {
		type: 'promotion_banner';
		endpoint: string;
	}
}

export interface RealView extends View {
	type: "grid" | "swimlanes"
}

export interface RealBanner extends Banner {
	uuid: Banner['_uuid'];
	view: RealView;
}

export interface Aisle {
	id: number;
	icon: ImageDetails,
	hero_image: ImageDetails,
	title: string;
	endpoint: string;
}

export interface AisleLayoutModelBase {
	layout: AisleLayoutModel[];
}

export interface AisleLayoutModel {
	type: string;
	sort: number;
	title:	string;
	product_selection: RequireAtLeastOne<{ aisle_id: number; subaisle_id: number; catalogue_keys: string[]; }>

	//programatic
	store_code?: string | null;
}

export interface AisleLayoutBase {
	layout_items: AisleLayout[];
}

export interface AisleLayout extends AisleLayoutModel {
	products: ProductData[];
	// metadata: { ... };
}

export interface ProductData extends ProductDisplayDetails { 
	[k:string]: any;
}

// Can't use the protos definition because deep down, backend actually used product_codes
// (no store code on the product), not catalogue keys
export type GetBuyAgainItemsResponse = Omit<GetBuyAgainItemsResponseProto, "product_codes"> & {
	catalogue_keys: string[],
};

/**
 * A list of tags that can appear on a product tile
 */
export enum ProductTags {
	BuyAgain = "buyAgain",
}

export enum ProductType {
	Combo = "combo",
	Single = "single",
}

export function sortProductsByType(items: ProductData[] | undefined, types: ProductType[]): ProductData[] {

	if (!items) return [];
	
	const stockIn: Record<string, ProductData[]> = { '*': [] };
	const stockOut: Record<string, ProductData[]> = { '*': [] };

	types.forEach(t => {
		stockIn[t] = [];
		stockOut[t] = [];
	});
	items.forEach(i => {
		const { product_type: t } = i;
		if (types.includes(t as ProductType))
			i.in_stock ? stockIn[t].push(i) : stockOut[t].push(i)
		else
			i.in_stock ? stockIn['*'].push(i) : stockOut['*'].push(i)
	});

	types.push('*' as ProductType);
	const sorted: ProductData[] = [];
	types.forEach(t => sorted.push(...stockIn[t]));
	types.forEach(t => sorted.push(...stockOut[t]));

	return sorted;
}

// ############################################################
// SEARCH
// ############################################################
export enum SearchView {
	NoSearchTerm = 'no_search_term',
	MatchingSearchResult = 'matching_search_result',
	NoSearchResult = 'no_search_result',
}

export interface GrocResults {
	view: SearchView;
	aisles: Aisle[]; // normally for no
	groceries: ProductData[]; // normally string-matched
	title: string;
}

export type FoodResultCommonPayload = {
	name: string,
	description: string,
	count: number,
	image_url: string | null,
	display_style: "single" | "horizontal_squares" | "horizontal_circles",
	ids: number[],
}

export interface TagItem { // "tag", "recommended_tag"
	name: string;
	count: number;
	ids: number[];
	image_url: string;
}

interface RestaurantGroupItem { // "recommended_restaurant_group"
	group_id: number;
	ids: number[];
	image_url: string;
}

interface RestaurantItem { // "recommended_restaurant", "restaurant"
	id: number;
	name: string;
	group_id: number;
	group_name: string;
	image_url: string;
	logo_url: string;
	number_of_ratings: number;
	number_of_ratings_str: string;
	ratings: number;
	ratings_star_colour: string;
	tag_string: string;
}

export enum FoodResultType {
	Tag = "tag",
	RecommendedTag = "recommended_tag",
	Restaurant = "restaurant",
	RecommendedRestaurant = "recommended_restaurant",
	RecommendedRestaurantGroup = "recommended_restaurant_group"
}

type Tag = FoodResultType.Tag | FoodResultType.RecommendedTag;
type Restaurant = FoodResultType.Restaurant | FoodResultType.RecommendedRestaurant;
type RestaurantGroup = FoodResultType.RecommendedRestaurantGroup;

interface TagsFoodResultItems {
	item_type: Tag,
	items: TagItem[],
}

interface RestaurantsFoodResultItems {
	item_type: Restaurant,
	items: RestaurantItem[],
}

interface RestaurantGroupsFoodResultItems {
	item_type: RestaurantGroup,
	items: RestaurantGroupItem[],
}

export type TagsFoodResultPayload = FoodResultCommonPayload & TagsFoodResultItems;
export type RestaurantsFoodResultPayload = FoodResultCommonPayload & RestaurantsFoodResultItems;
export type RestaurantGroupsFoodResultPayload = FoodResultCommonPayload & RestaurantGroupsFoodResultItems;
export type FoodResultPayload = TagsFoodResultPayload | RestaurantsFoodResultPayload | RestaurantGroupsFoodResultPayload;

export interface FoodResults {
	view: SearchView;
	title: string | null;
	subtitle: string | null;
	ml_generated: boolean;
	image_url: string | null;
	items: {
		tiles: {
			type: "collection", // TODO: Any other possible values?
			payload: FoodResultPayload,
		}[],
	}[];
}
